import React, { Component } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import './PendingList.scss';
import MerchantDetailsCard from '../MerchantDetailsCard';
import Dropzone from 'react-dropzone';
import { toast } from 'react-toastify';
import {
    EmvioValidatedForm,
    EmvioValidatedInput,
    EmvioButton
} from '@nexio/emvio-form-components';
import { EmvioLoadingSpinner as LoadingSpinner } from '@nexio/emvio-util-ui';
import uploadFileIcon from '../../../images/upload-file-icon.svg';
import deleteTrashIcon from '../../../images/delete-trash-icon.svg';
import { updatePendingListItem, getPendingListDownloadUrls, getPendingListUploadLinks, getMerchantDetails as queryForMerchantDetails, deletePendingListDocument, submitPendingList } from '../merchantDetailsActions';
import { toastIfError } from '../../../utils/toast';
import { doesPropExistAndHasItChanged } from '@nexio/emvio-util-app';

const maxFileSizeLimit = 10 * 1024 * 1024; // 10MB
const maxFilesPerCategory = 10;

/**
 * Uploads a file to S3 using a presigned URL
 * @param {File} file - The file to upload
 * @param {string} presignedUrl - The presigned S3 URL
 * @returns {Promise} - Resolves on success, rejects on error
 */
const uploadFileToS3 = (file, presignedUrl) => {
    return fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
            'Content-Type': file.type || 'application/octet-stream'
        }
    }).then((response) => {
        if (!response.ok) {
            throw new Error(`Failed to upload file: ${file.name}`);
        }

        return response;
    });
};

export class PendingList extends Component {
    static propTypes = {
        merchantDetails: PropTypes.object,
        match: PropTypes.object.isRequired,
        onUpdatePendingListItem: PropTypes.func.isRequired,
        onGetMerchantDetails: PropTypes.func.isRequired,
        onDeletePendingListDocument: PropTypes.func.isRequired,
        onSubmitPendingList: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);
        this.state = {
            documents: {},
            underwriterNotes: '',
            questionResponses: {},
            pendingUploads: {}, // Track files waiting to be uploaded: { categoryId: [files] }
            pendingDownloadUrlRefresh: null // Track categoryId that needs download URL refresh after update completes
        };
        this.dropzoneRefs = {};
    }

    componentDidMount() {
        this.initializeQuestionResponses();
        this.initializeDocuments();
    }

    componentDidUpdate(prevProps) {
        toastIfError(prevProps, this.props, 'updatePendingListItemError');
        toastIfError(prevProps, this.props, 'getPendingListUploadLinksError');
        toastIfError(prevProps, this.props, 'deletePendingListDocumentError');
        toastIfError(prevProps, this.props, 'submitPendingListError');

        if (doesPropExistAndHasItChanged(prevProps, this.props, 'merchantDetails.pendingData')) {
            this.initializeQuestionResponses();
            this.initializeDocuments();
        }

        // Handle upload links received
        if (prevProps.uploadLinks === null && this.props.uploadLinks !== null && !this.props.getPendingListUploadLinksError) {
            this.handleUploadLinksReceived();
        }

        // Refetch after successful document deletion
        if (prevProps.isDeletePendingListDocumentLoading && !this.props.isDeletePendingListDocumentLoading && !this.props.deletePendingListDocumentError) {
            const onboardingId = this.props.match?.params?.onboardingId;
            if (onboardingId) {
                this.props.onGetMerchantDetails(onboardingId);
            }
        }

        // Handle successful pending list submission
        if (prevProps.isSubmitPendingListLoading && !this.props.isSubmitPendingListLoading && !this.props.submitPendingListError) {
            toast.success('Pending list submitted successfully');
            const onboardingId = this.props.match?.params?.onboardingId;
            if (onboardingId) {
                this.props.onGetMerchantDetails(onboardingId);
            }
        }

        // Fetch download URLs after pending list item update completes successfully
        if (prevProps.isUpdatePendingListItemLoading && !this.props.isUpdatePendingListItemLoading && !this.props.updatePendingListItemError) {
            const { pendingDownloadUrlRefresh } = this.state;
            if (pendingDownloadUrlRefresh) {
                this.props.onGetPendingListDownloadUrls(pendingDownloadUrlRefresh);
                this.setState({ pendingDownloadUrlRefresh: null });
            }
        }
    }

    initializeQuestionResponses = () => {
        const { merchantDetails } = this.props;
        const pendingData = merchantDetails?.pendingData || [];
        const questionResponses = {};

        pendingData
            .filter((item) => item.requirementType === 1)
            .forEach((item) => {
                questionResponses[item.id] = item.questionResponse || '';
            });

        this.setState({ questionResponses });
    };

    initializeDocuments = () => {
        const { merchantDetails } = this.props;
        const pendingData = merchantDetails?.pendingData || [];
        const documents = {};

        pendingData
            .filter((item) => item.requirementType === 0 && item.documentUploads)
            .forEach((item) => {
                try {
                    this.props.onGetPendingListDownloadUrls(item.id);
                    const filenames = JSON.parse(item.documentUploads);
                    if (filenames && filenames.length > 0) {
                        // Convert filenames to file-like objects with a name property
                        documents[item.id] = filenames.map((filename) => ({ name: filename }));
                    }
                } catch (e) {
                    console.error(`Failed to parse filename: ${e}`);
                    // If parsing fails, skip this item
                }
            });

        this.setState({ documents });
    };

    onDrop = (categoryId) => (acceptedFiles, rejectedFiles) => {
        if (!_.isEmpty(rejectedFiles)) {
            toast.error('Please ensure files are in an accepted format (PDF, JPG, PNG).');

            return;
        }

        const currentFiles = this.state.documents[categoryId] || [];

        const filteredCurrentFiles = _.differenceBy(currentFiles, acceptedFiles, 'name');

        const totalFilesAfterAdd = filteredCurrentFiles ? filteredCurrentFiles.length + acceptedFiles.length : 0;

        if (totalFilesAfterAdd > maxFilesPerCategory) {
            const allowedFiles = maxFilesPerCategory - (filteredCurrentFiles ? filteredCurrentFiles.length : 0);
            if (allowedFiles > 0) {
                acceptedFiles = acceptedFiles.slice(0, allowedFiles);
            } else {
                toast.error(`Maximum ${maxFilesPerCategory} files allowed per category.`);

                return;
            }
        }

        const newFiles = acceptedFiles.filter((file) => {
            if (file.size > maxFileSizeLimit) {
                toast.error(`File ${file.name} is too large. Maximum size is 10MB.`);

                return false;
            }

            return true;
        });

        if (newFiles && newFiles.length === 0) {
            return;
        }

        // Add files to state (with duplicates removed from current files)
        this.setState((prevState) => ({
            documents: {
                ...prevState.documents,
                [categoryId]: [...filteredCurrentFiles, ...newFiles]
            },
            pendingUploads: {
                ...prevState.pendingUploads,
                [categoryId]: newFiles
            }
        }));

        // Trigger upload flow
        this.startUploadFlow(categoryId, newFiles);
    };

    startUploadFlow = (categoryId, files) => {
        const filenames = files.map((file) => file.name);
        this.props.onGetPendingListUploadLinks(categoryId, filenames);
    };

    handleUploadLinksReceived = async () => {
        const { uploadLinks } = this.props;
        const { pendingUploads } = this.state;

        if (!uploadLinks || !Array.isArray(uploadLinks) || (uploadLinks && uploadLinks.length === 0)) {
            return;
        }

        // Find which category this upload is for by matching filenames
        let categoryId = null;
        let filesToUpload = [];

        for (const [id, files] of Object.entries(pendingUploads)) {
            const filenames = files.map((f) => f.name);
            const uploadFilenames = uploadLinks.map((link) => link.filename);

            if (filenames && uploadFilenames && filenames.length === uploadFilenames.length &&
                filenames.every((name) => uploadFilenames.includes(name))) {
                categoryId = id;
                filesToUpload = files;
                break;
            }
        }

        if (!categoryId || filesToUpload.length ? filesToUpload.length === 0 : false) {
            return;
        }

        try {
            // Create a map of filename to presigned URL
            const urlMap = {};
            uploadLinks.forEach((link) => {
                urlMap[link.filename] = link.url;
            });

            // Upload all files to S3 in parallel
            const uploadPromises = filesToUpload.map((file) => {
                const presignedUrl = urlMap[file.name];
                if (!presignedUrl) {
                    throw new Error(`No presigned URL found for file: ${file.name}`);
                }

                return uploadFileToS3(file, presignedUrl);
            });

            await Promise.all(uploadPromises);

            // Get existing documentUploads and merge with new filenames
            const documentQuestions = this.getDocumentQuestions();
            const documentCategory = documentQuestions.find((item) => item.id === categoryId);
            let existingFilenames = [];

            if (documentCategory?.documentUploads) {
                try {
                    existingFilenames = JSON.parse(documentCategory.documentUploads);
                } catch (e) {
                    // If parsing fails, treat as empty array
                    existingFilenames = [];
                }
            }

            // Merge existing with new filenames (avoid duplicates)
            const newFilenames = filesToUpload.map((file) => file.name);
            const allFilenames = [...new Set([...existingFilenames, ...newFilenames])];

            // Update pending list item with merged filenames
            this.props.onUpdatePendingListItem(categoryId, undefined, allFilenames);

            // Track that we need to refresh download URLs after the update completes
            // Clear pending uploads for this category
            this.setState((prevState) => {
                const newPendingUploads = { ...prevState.pendingUploads };
                delete newPendingUploads[categoryId];

                return {
                    pendingUploads: newPendingUploads,
                    pendingDownloadUrlRefresh: categoryId
                };
            });

            toast.success(`Successfully uploaded ${filesToUpload.length ? filesToUpload.length : 0} file(s)`);
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(`Failed to upload files: ${error.message}`);

            // Remove failed files from state
            this.setState((prevState) => {
                const currentFiles = prevState.documents[categoryId] || [];
                const failedFilenames = filesToUpload.map((f) => f.name);
                const remainingFiles = currentFiles.filter((file) => !failedFilenames.includes(file.name));

                const newPendingUploads = { ...prevState.pendingUploads };
                delete newPendingUploads[categoryId];

                return {
                    documents: {
                        ...prevState.documents,
                        [categoryId]: remainingFiles
                    },
                    pendingUploads: newPendingUploads
                };
            });
        }
    };

    onOpenFileDialog = (categoryId) => () => {
        if (this.dropzoneRefs[categoryId]) {
            this.dropzoneRefs[categoryId].open();
        }
    };

    removeFile = (categoryId, filename) => (e) => {
        e.preventDefault();
        e.stopPropagation();

        const currentFiles = this.state.documents[categoryId] || [];
        const updatedFiles = currentFiles.filter((file) => file.name !== filename);

        // Update local state
        this.setState((prevState) => ({
            documents: {
                ...prevState.documents,
                [categoryId]: updatedFiles
            }
        }));

        // Delete the document using the dedicated DELETE endpoint
        this.props.onDeletePendingListDocument(categoryId, filename);
    };

    handleNotesChange = (e) => {
        this.setState({ underwriterNotes: e.target.value });
    };

    isDocumentLoading = (categoryId, file) => {
        const pendingFiles = this.state.pendingUploads[categoryId] || [];
        const isUploading = pendingFiles.some((f) => f.name === file.name);

        return !isUploading; // Return true if NOT uploading (safe to show delete)
    };

    renderDocumentRow = (documentCategory) => {
        const downloadUrls = this.props.downloadUrls?.[documentCategory.id] || [];
        const files = this.state.documents?.[documentCategory.id] || [];
        const isPending = this.props.merchantDetails?.detail?.status === 'Pending';

        return (
            <div key={documentCategory.id} className="document-upload-row">
                <div className="document-info">
                    <div className="document-label">{documentCategory.label}</div>
                    {documentCategory.description && (
                        <div className="document-tooltip-trigger">
                            <span className="tooltip-text">Why are you asking me this?</span>
                            <div className="tooltip-content">{documentCategory.description}</div>
                        </div>
                    )}
                </div>
                <div className="document-action">
                    <Dropzone
                        accept="application/pdf,image/jpeg,image/png"
                        disableClick
                        disabled={!isPending}
                        multiple
                        onDrop={this.onDrop(documentCategory.id)}
                        ref={(node) => {
                            this.dropzoneRefs[documentCategory.id] = node;
                        }}
                        style={{ border: 'none' }}
                    >
                        {files && files.length > 0 ? (
                            <div className="uploaded-files-wrapper">
                                <div className="uploaded-files">
                                    {files.map((file) => (
                                        <div key={`${documentCategory.id}-${file.name}`} className="uploaded-file">
                                            <a
                                                href={downloadUrls.find((url) => url.filename === file.name)?.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="file-name"
                                            >
                                                {file.name}
                                            </a>
                                            {isPending && (
                                                this.isDocumentLoading(documentCategory.id, file) ? (
                                                    <button
                                                        type="button"
                                                        className="remove-file"
                                                        onClick={this.removeFile(documentCategory.id, file.name)}
                                                        aria-label="Remove file"
                                                    >
                                                        <img src={deleteTrashIcon} alt="Delete" className="delete-icon" />
                                                    </button>
                                                ) : (
                                                    <span className="file-uploading-spinner">
                                                        <LoadingSpinner size="small" relative />
                                                    </span>
                                                )
                                            )}
                                        </div>
                                    ))}
                                </div>
                                {isPending && (
                                    <button
                                        type="button"
                                        className="add-document-link"
                                        onClick={this.onOpenFileDialog(documentCategory.id)}
                                    >
                                        Add document
                                    </button>
                                )}
                            </div>
                        ) : (
                            isPending && (
                                <EmvioButton
                                    className="upload-button"
                                    type="button"
                                    onClick={this.onOpenFileDialog(documentCategory.id)}
                                >
                                    <img src={uploadFileIcon} alt="" className="upload-icon" />
                                    Upload Documents
                                </EmvioButton>
                            )
                        )}
                    </Dropzone>
                </div>
            </div>
        );
    };

    getInputQuestions = () => {
        const { merchantDetails } = this.props;
        const pendingData = merchantDetails?.pendingData || [];

        return pendingData
            .filter((item) => item.requirementType === 1)
            .map((item) => ({
                id: item.id,
                name: item.taskId?.toString(),
                label: item.title || '',
                description: item.reason || '',
                taskId: item.taskId,
                questionResponse: item.questionResponse
            }));
    };

    getDocumentQuestions = () => {
        const { merchantDetails } = this.props;
        const pendingData = merchantDetails?.pendingData || [];

        return pendingData
            .filter((item) => item.requirementType === 0)
            .map((item) => ({
                id: item.id,
                label: item.title || '',
                description: item.reason || '',
                documentUploads: item.documentUploads
            }));
    };

    allItemsAreCompleted = () => {
        const { merchantDetails } = this.props;
        const pendingData = merchantDetails?.pendingData || [];

        for (const item of pendingData) {
            if (item.requirementType === 0) {
                if (!item.documentUploads) {
                    return false;
                }
            } else if (item.requirementType === 1) {
                if (!item.questionResponse) {
                    return false;
                }
            }
        }

        return true;
    };

    handleQuestionResponseChange = (itemId) => (e) => {
        this.setState((prevState) => ({
            questionResponses: {
                ...prevState.questionResponses,
                [itemId]: e.target.value
            }
        }));
    };

    handleUpdateQuestionItem = (itemId) => () => {
        const questionResponse = itemId in this.state.questionResponses
            ? this.state.questionResponses[itemId]
            : '';
        this.props.onUpdatePendingListItem(itemId, questionResponse);
    };

    handleSubmitPendingList = () => {
        const { merchantDetails } = this.props;
        const cmcMerchantId = merchantDetails?.detail?.cmcMerchantId;

        if (cmcMerchantId) {
            this.props.onSubmitPendingList(cmcMerchantId);
        } else {
            toast.error('Unable to submit: Merchant ID not found');
        }
    };

    render() {
        const { merchantDetails } = this.props;
        const inputQuestions = this.getInputQuestions();
        const documentQuestions = this.getDocumentQuestions();
        const isPending = merchantDetails?.detail?.status === 'Pending';

        return (
            <MerchantDetailsCard title="Pending List" startOpen={isPending}>
                <div className="emvio-pendinglist">
                    <div className="emvio-pendinglist-content">
                        <p>This is the list of pending items needed before the underwriters can proceed with the application.
                            All items must be completed before it can be submitted back to underwriting. You may fill out any or all information and submit it or send it to the merchant for them to fill out.
                            The merchant will be able to change any information you pre-fill in here before sending. The Merchant will only see your note and the pending items on the list.</p>
                    </div>
                    <EmvioValidatedForm className="pending-list-content">
                        <div className="input-fields">
                            {inputQuestions.map((questionItem) => (
                                <div key={questionItem.id} className="input-row">
                                    <div className="input-label-row">
                                        <span className="input-label">{questionItem.label || ''}</span>
                                        {questionItem.description && (
                                            <div className="input-tooltip-trigger">
                                                <span className="tooltip-text">Why are you asking me this?</span>
                                                <div className="tooltip-content">{questionItem.description}</div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="input-wrapper">
                                        <EmvioValidatedInput
                                            disabled={!isPending}
                                            name={questionItem.name}
                                            value={questionItem.id in this.state.questionResponses
                                                ? this.state.questionResponses[questionItem.id]
                                                : (questionItem.questionResponse ?? '')}
                                            onChange={this.handleQuestionResponseChange(questionItem.id)}
                                            onBlur={this.handleUpdateQuestionItem(questionItem.id)}
                                            placeholder="Answer here"
                                            shouldNotTrim
                                            type="text"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="document-upload-section">
                            {documentQuestions.map((documentItem) => this.renderDocumentRow(documentItem))}
                        </div>

                        {/*
                        TODO: Wire up notes when we have a place to up them in the backend
                        <div className="underwriter-notes">
                            <EmvioValidatedTextArea
                                disabled={!isPending}
                                name="underwriterNotes"
                                placeholder="Add notes here."
                                value={this.state.underwriterNotes}
                                onChange={this.handleNotesChange}
                            />
                        </div> */}

                        {isPending && (
                            <div className="pending-list-actions">
                                <EmvioButton
                                    className="submit-button"
                                    onClick={this.handleSubmitPendingList}
                                    disabled={!this.allItemsAreCompleted() || this.props.isSubmitPendingListLoading}
                                >
                                    {this.props.isSubmitPendingListLoading ? 'Submitting...' : 'Submit'}
                                </EmvioButton>
                            </div>
                        )}
                    </EmvioValidatedForm>
                </div>
            </MerchantDetailsCard>
        );
    }
}

function mapStateToProps(state) {
    return {
        isUpdatePendingListItemLoading: state.entities.merchantDetails?.updatePendingListItemLoading || false,
        updatePendingListItemError: state.entities.merchantDetails?.updatePendingListItemError || null,
        isGetPendingListUploadLinksLoading: state.entities.merchantDetails?.getPendingListUploadLinksLoading || false,
        getPendingListUploadLinksError: state.entities.merchantDetails?.getPendingListUploadLinksError || null,
        uploadLinks: state.entities.merchantDetails?.uploadLinks || null,
        isDeletePendingListDocumentLoading: state.entities.merchantDetails?.deletePendingListDocumentLoading || false,
        deletePendingListDocumentError: state.entities.merchantDetails?.deletePendingListDocumentError || null,
        isSubmitPendingListLoading: state.entities.merchantDetails?.submitPendingListLoading || false,
        submitPendingListError: state.entities.merchantDetails?.submitPendingListError || null,
        downloadUrls: state.entities.merchantDetails?.downloadUrls || null
    };
}

function mapDispatchToProps(dispatch) {
    return {
        onUpdatePendingListItem: (itemId, questionResponse, documentUploads) => dispatch(updatePendingListItem(itemId, questionResponse, documentUploads)),
        onGetMerchantDetails: (onboardingId) => dispatch(queryForMerchantDetails(onboardingId)),
        onGetPendingListUploadLinks: (itemId, filenames) => dispatch(getPendingListUploadLinks(itemId, filenames)),
        onDeletePendingListDocument: (pendingListItemId, documentName) => dispatch(deletePendingListDocument(pendingListItemId, documentName)),
        onSubmitPendingList: (cmcMerchantId) => dispatch(submitPendingList(cmcMerchantId)),
        onGetPendingListDownloadUrls: (itemId) => dispatch(getPendingListDownloadUrls(itemId))
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(PendingList));
